<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" lg="11" xl="10">
        <v-alert
          v-if="!isUserAdmin"
          type="error"
          outlined
          class="mt-8"
        >
          You are not allowed to access resolver diagnostics.
        </v-alert>

        <v-card v-else class="mt-8 mb-8" :loading="stateLoading">
          <template slot="progress">
            <v-progress-linear
              color="#006c27"
              indeterminate
              height="6"
            ></v-progress-linear>
          </template>

          <v-card-title class="grey--text text--darken-2">
            Identity Resolver Diagnostics
            <v-spacer />
            <v-btn
              color="#006c27"
              dark
              class="mr-2"
              :loading="triggerLoading"
              @click="triggerRefresh"
            >
              Refresh Now
            </v-btn>
            <v-btn text color="#006c27" @click="loadState">
              Reload
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row align="stretch">
              <v-col cols="12" md="6">
                <v-sheet
                  class="pa-4 diag-header-card"
                  :class="cacheState.ready ? 'diag-header-card--success' : 'diag-header-card--danger'"
                  outlined
                  rounded
                >
                  <div class="diag-section-title">
                    <v-icon small color="#006c27" class="mr-1">mdi-database-search</v-icon>
                    Resolver Cache Snapshot
                  </div>
                  <div class="diag-chip-row diag-chip-row--stacked">
                    <v-chip
                      small
                      dark
                      :color="cacheState.ready ? '#2e7d32' : '#c62828'"
                    >
                      <v-icon left small>
                        {{ cacheState.ready ? "mdi-check-circle" : "mdi-alert-circle" }}
                      </v-icon>
                      {{ cacheState.ready ? "Ready" : "Not Ready" }}
                    </v-chip>
                    <v-chip
                      small
                      dark
                      :color="refreshStateColor(activeRefreshState)"
                    >
                      <v-icon left small>
                        {{ refreshStateIcon(activeRefreshState) }}
                      </v-icon>
                      {{ activeRefreshState }}
                    </v-chip>
                  </div>
                  <div class="diag-metric-meta" v-if="activeStage">
                    Stage: <strong>{{ activeStage }}</strong>
                  </div>
                  <div class="diag-metric-meta" v-else>
                    Stage: -
                  </div>
                  <div
                    v-if="!cacheState.ready && cacheState.readyReason"
                    class="diag-metric-error"
                  >
                    {{ cacheState.readyReason }}
                  </div>
                  <div class="diag-stat-grid mt-3">
                    <div class="diag-stat-item">
                      <div class="diag-stat-value">{{ cacheState.cacheOwnerRefs || 0 }}</div>
                      <div class="diag-stat-label">Owner refs</div>
                    </div>
                    <div class="diag-stat-item">
                      <div class="diag-stat-value">{{ cacheState.cacheOids || 0 }}</div>
                      <div class="diag-stat-label">OIDs</div>
                    </div>
                    <div class="diag-stat-item">
                      <div class="diag-stat-value">{{ cacheState.authDecisionCount || 0 }}</div>
                      <div class="diag-stat-label">Auth decisions</div>
                    </div>
                  </div>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="6">
                <v-sheet class="pa-4 diag-header-card diag-header-card--neutral" outlined rounded>
                  <div class="diag-section-title">
                    <v-icon small color="#5d4037" class="mr-1">mdi-history</v-icon>
                    Resolver Runs
                  </div>

                  <v-row dense class="diag-run-columns">
                    <v-col cols="12" md="6">
                      <div class="diag-run-block">
                        <div class="diag-run-title">
                          <v-icon small color="#1565c0" class="mr-1">mdi-timer-sand</v-icon>
                          Current Run
                        </div>
                        <div class="diag-kv-grid">
                          <div class="diag-kv-key">Run ID</div>
                          <div class="diag-kv-value">{{ currentRun.runId || "-" }}</div>
                          <div class="diag-kv-key">Trigger</div>
                          <div class="diag-kv-value">{{ currentRun.trigger || "-" }}</div>
                          <div class="diag-kv-key">Started</div>
                          <div class="diag-kv-value">{{ formatDate(currentRun.startedAt) }}</div>
                        </div>
                        <div v-if="currentRun.total > 0" class="diag-progress-wrap">
                          <div class="diag-progress-label">
                            <span>Progress</span>
                            <span>{{ currentRun.processed }}/{{ currentRun.total }} ({{ formatPercent(currentRun.percent) }})</span>
                          </div>
                          <v-progress-linear
                            :value="currentRun.percent || 0"
                            color="#1565c0"
                            background-color="#dbeaf8"
                            height="8"
                            rounded
                          />
                        </div>
                        <div class="diag-metric-error" v-if="currentRun.error">
                          Error: {{ currentRun.error }}
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="diag-run-block">
                        <div class="diag-run-title">
                          <v-icon small color="#5d4037" class="mr-1">mdi-archive-clock</v-icon>
                          Last Run
                        </div>
                        <div
                          class="diag-chip-row"
                          v-if="lastRun.state && lastRun.state.toLowerCase() !== 'idle'"
                        >
                          <v-chip
                            small
                            dark
                            :color="refreshStateColor(lastRun.state || 'idle')"
                          >
                            <v-icon left small>
                              {{ refreshStateIcon(lastRun.state || "idle") }}
                            </v-icon>
                            {{ lastRun.state || "idle" }}
                          </v-chip>
                        </div>
                        <div class="diag-kv-grid">
                          <div class="diag-kv-key">Run ID</div>
                          <div class="diag-kv-value">{{ lastRun.runId || "-" }}</div>
                          <div class="diag-kv-key">Trigger</div>
                          <div class="diag-kv-value">{{ lastRun.trigger || "-" }}</div>
                          <div class="diag-kv-key">Started</div>
                          <div class="diag-kv-value">{{ formatDate(lastRun.startedAt) }}</div>
                          <div class="diag-kv-key">Finished</div>
                          <div class="diag-kv-value">{{ formatDate(lastRun.finishedAt) }}</div>
                          <div class="diag-kv-key">Duration</div>
                          <div class="diag-kv-value">{{ formatDuration(lastRun.durationMs) }}</div>
                        </div>
                        <div class="diag-metric-error" v-if="lastRun.error">
                          Error: {{ lastRun.error }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <v-tabs v-model="activeTab" color="#006c27" background-color="transparent">
              <v-tab>Resolver Cache</v-tab>
              <v-tab>Application Owner References</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab" class="mt-3">
              <v-tab-item>
                <v-row align="center" class="resolver-search-row">
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="query"
                      label="Search by oid, ownerRef, email, name, ext14"
                      clearable
                      outlined
                      dense
                      hide-details
                      @keyup.enter="search"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center">
                    <v-checkbox
                      v-model="cacheUnresolvedOnly"
                      label="Unresolved only"
                      hide-details
                      class="ma-0 pa-0"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center">
                    <v-checkbox
                      v-model="cacheAmbiguousOnly"
                      label="Ambiguous only"
                      hide-details
                      class="ma-0 pa-0"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center justify-end">
                    <v-btn
                      color="#006c27"
                      dark
                      class="mr-2"
                      :loading="searchLoading"
                      @click="search"
                    >
                      Search
                    </v-btn>
                    <v-btn text color="#006c27" @click="resetSearch">Reset</v-btn>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headers"
                  :items="searchItems"
                  :loading="searchLoading"
                  :items-per-page="pageSize"
                  hide-default-footer
                  class="elevation-1"
                  @click:row="openCacheDetail"
                >
                  <template v-slot:[`item.auth`]="{ item }">
                    <div class="text-caption">
                      E: {{ boolText(item.authDecision.isEligibleUser) }} |
                      A: {{ boolText(item.authDecision.isAdminGroupMember) }} |
                      B: {{ boolText(item.authDecision.isBetaGroupMember) }} |
                      I: {{ boolText(item.authDecision.isInternalVisibilityMember) }}
                    </div>
                  </template>
                  <template v-slot:[`item.unresolved`]="{ item }">
                    <div class="text-caption">
                      {{ item.projection.isUnresolved ? "yes" : "no" }}
                    </div>
                  </template>
                  <template v-slot:[`item.unresolvedReason`]="{ item }">
                    <div class="text-caption">{{ item.projection.unresolvedReasonCode || "-" }}</div>
                  </template>
                  <template v-slot:[`item.ambiguous`]="{ item }">
                    <div class="text-caption">
                      {{ item.projection.isAmbiguous ? "yes" : "no" }}
                    </div>
                  </template>
                  <template v-slot:[`item.candidateCount`]="{ item }">
                    <div class="text-caption">{{ item.projection.candidateCount || "-" }}</div>
                  </template>
                  <template v-slot:[`item.selection`]="{ item }">
                    <div class="text-caption">{{ item.projection.primarySelectionReason || "-" }}</div>
                  </template>
                  <template slot="no-data">
                    <div v-if="searchLoading">Loading cache entries ...</div>
                    <div v-else>No cache entries found.</div>
                  </template>
                </v-data-table>

                <div class="d-flex align-center justify-end mt-3">
                  <v-btn
                    text
                    :disabled="page <= 1 || searchLoading"
                    @click="changePage(page - 1)"
                  >
                    Previous
                  </v-btn>
                  <div class="text-caption mx-3">Page {{ page }} / {{ maxPage }}</div>
                  <v-btn
                    text
                    :disabled="page >= maxPage || searchLoading"
                    @click="changePage(page + 1)"
                  >
                    Next
                  </v-btn>
                </div>
              </v-tab-item>

              <v-tab-item>
                <v-row align="center" class="resolver-search-row">
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="appOwnersQuery"
                      label="Search app owners by app id, owner ref, projection fields"
                      clearable
                      outlined
                      dense
                      hide-details
                      @keyup.enter="searchAppOwners"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center">
                    <v-checkbox
                      v-model="appOwnersUnresolvedOnly"
                      label="Unresolved only"
                      hide-details
                      class="ma-0 pa-0"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center">
                    <v-checkbox
                      v-model="appOwnersAmbiguousOnly"
                      label="Ambiguous only"
                      hide-details
                      class="ma-0 pa-0"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center justify-end">
                    <v-btn
                      color="#006c27"
                      dark
                      class="mr-2"
                      :loading="appOwnersLoading"
                      @click="searchAppOwners"
                    >
                      Search
                    </v-btn>
                    <v-btn text color="#006c27" @click="resetAppOwnerSearch">Reset</v-btn>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="appOwnerHeaders"
                  :items="appOwnerItems"
                  :loading="appOwnersLoading"
                  :items-per-page="appOwnersPageSize"
                  hide-default-footer
                  class="elevation-1"
                  @click:row="openAppOwnerDetail"
                >
                  <template v-slot:[`item.unresolved`]="{ item }">
                    <div class="text-caption">
                      {{ item.projection.isUnresolved ? "yes" : "no" }}
                    </div>
                  </template>
                  <template v-slot:[`item.reason`]="{ item }">
                    <div class="text-caption">{{ item.projection.unresolvedReasonCode || "-" }}</div>
                  </template>
                  <template v-slot:[`item.ambiguous`]="{ item }">
                    <div class="text-caption">
                      {{ item.projection.isAmbiguous ? "yes" : "no" }}
                    </div>
                  </template>
                  <template v-slot:[`item.candidateCount`]="{ item }">
                    <div class="text-caption">{{ item.projection.candidateCount || "-" }}</div>
                  </template>
                  <template v-slot:[`item.selection`]="{ item }">
                    <div class="text-caption">{{ item.projection.primarySelectionReason || "-" }}</div>
                  </template>
                  <template slot="no-data">
                    <div v-if="appOwnersLoading">Loading app owner references ...</div>
                    <div v-else>No app owner entries found.</div>
                  </template>
                </v-data-table>

                <div class="d-flex align-center justify-end mt-3">
                  <v-btn
                    text
                    :disabled="appOwnersPage <= 1 || appOwnersLoading"
                    @click="changeAppOwnerPage(appOwnersPage - 1)"
                  >
                    Previous
                  </v-btn>
                  <div class="text-caption mx-3">
                    Page {{ appOwnersPage }} / {{ appOwnersMaxPage }}
                  </div>
                  <v-btn
                    text
                    :disabled="appOwnersPage >= appOwnersMaxPage || appOwnersLoading"
                    @click="changeAppOwnerPage(appOwnersPage + 1)"
                  >
                    Next
                  </v-btn>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>

        <v-dialog v-model="detailDialog.visible" max-width="900">
          <v-card>
            <v-card-title class="text-h6 grey--text text--darken-2">
              {{ detailDialog.title }}
              <v-spacer />
              <v-btn icon @click="closeDetailDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row v-if="detailDialog.item" dense>
                <v-col cols="12" md="6">
                  <div class="text-caption grey--text">Source</div>
                  <div class="mb-3">{{ detailDialog.sourceLabel }}</div>
                  <div class="text-caption grey--text">Primary Key</div>
                  <div v-if="detailDialog.source === 'cache'" class="mb-3">
                    {{ detailDialog.item.oid || "-" }}
                  </div>
                  <div v-else class="mb-3">
                    {{ detailDialog.item.inputOwnerRef || "-" }}
                  </div>
                  <div class="text-caption grey--text">Projection OwnerRef</div>
                  <div class="mb-3">
                    {{ detailDialog.item.projection && detailDialog.item.projection.ownerRef
                      ? detailDialog.item.projection.ownerRef
                      : "-" }}
                  </div>
                  <div class="text-caption grey--text">Projection Email</div>
                  <div class="mb-3">
                    {{ detailDialog.item.projection && detailDialog.item.projection.email
                      ? detailDialog.item.projection.email
                      : "-" }}
                  </div>
                  <div class="text-caption grey--text">Projection OID</div>
                  <div class="mb-3">
                    {{ detailDialog.item.projection && detailDialog.item.projection.oid
                      ? detailDialog.item.projection.oid
                      : "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption grey--text">Application ID</div>
                  <div class="mb-3">
                    {{ detailDialog.item.applicationId || "-" }}
                  </div>
                  <div class="text-caption grey--text">Application Name</div>
                  <div class="mb-3">
                    {{ detailDialog.item.displayName || "-" }}
                  </div>
                  <div class="text-caption grey--text">Environment</div>
                  <div class="mb-3">
                    {{ detailDialog.item.environment || "-" }}
                  </div>
                  <div class="text-caption grey--text">Unresolved</div>
                  <div class="mb-3">
                    {{ detailDialog.item.projection && detailDialog.item.projection.isUnresolved ? "yes" : "no" }}
                  </div>
                  <div class="text-caption grey--text">Ambiguous</div>
                  <div class="mb-3">
                    {{ detailDialog.item.projection && detailDialog.item.projection.isAmbiguous ? "yes" : "no" }}
                  </div>
                  <div class="text-caption grey--text">Candidate Count</div>
                  <div class="mb-3">
                    {{ detailDialog.item.projection && detailDialog.item.projection.candidateCount
                      ? detailDialog.item.projection.candidateCount
                      : "-" }}
                  </div>
                  <div class="text-caption grey--text">Primary Selection</div>
                  <div class="mb-3">
                    {{ detailDialog.item.projection && detailDialog.item.projection.primarySelectionReason
                      ? detailDialog.item.projection.primarySelectionReason
                      : "-" }}
                  </div>
                </v-col>
              </v-row>
              <v-row v-if="detailDialog.item && detailDialog.item.projection && detailDialog.item.projection.isAmbiguous" dense>
                <v-col cols="12">
                  <div class="text-caption grey--text">Alternates</div>
                  <div
                    v-for="(line, lineIndex) in ambiguityAlternateLines(detailDialog.item.projection)"
                    :key="'resolver-ambiguity-line-' + lineIndex"
                    class="text-caption mb-1"
                  >
                    {{ line }}
                  </div>
                </v-col>
              </v-row>
              <v-divider class="my-3"></v-divider>
              <div class="text-subtitle-2 mb-2">Raw Details</div>
              <pre class="detail-json">{{ detailJson(detailDialog.item) }}</pre>
            </v-card-text>
          </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar.visible" :color="snackbar.color" timeout="3000">
          {{ snackbar.text }}
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import cass from "../api/cass";
import { formatAmbiguityAlternateLine } from "../utils/ownerProjectionAmbiguity";

export default {
  name: "ResolverDiagnostics",
  data() {
    return {
      stateLoading: false,
      searchLoading: false,
      appOwnersLoading: false,
      triggerLoading: false,
      query: "",
      appOwnersQuery: "",
      page: 1,
      pageSize: 20,
      total: 0,
      searchItems: [],
      cacheUnresolvedOnly: false,
      cacheAmbiguousOnly: false,
      appOwnersPage: 1,
      appOwnersPageSize: 20,
      appOwnersTotal: 0,
      appOwnerItems: [],
      appOwnersUnresolvedOnly: false,
      appOwnersAmbiguousOnly: false,
      activeTab: 0,
      detailDialog: {
        visible: false,
        source: "",
        sourceLabel: "",
        title: "",
        item: null,
      },
      cacheState: {
        ready: false,
        readyReason: "",
        cacheOwnerRefs: 0,
        cacheOids: 0,
        authDecisionCount: 0,
      },
      refreshStatus: {
        current: {},
        last: {},
      },
      pollHandle: null,
      headers: [
        { text: "OID", value: "oid", sortable: false },
        { text: "Owner Ref", value: "projection.ownerRef", sortable: false },
        { text: "Email", value: "projection.email", sortable: false },
        { text: "Display Name", value: "projection.displayName", sortable: false },
        { text: "Auth", value: "auth", sortable: false },
        { text: "Unresolved", value: "unresolved", sortable: false },
        { text: "Reason", value: "unresolvedReason", sortable: false },
        { text: "Ambiguous", value: "ambiguous", sortable: false },
        { text: "Candidates", value: "candidateCount", sortable: false },
        { text: "Selection", value: "selection", sortable: false },
      ],
      appOwnerHeaders: [
        { text: "App ID", value: "applicationId", sortable: false },
        { text: "App Name", value: "displayName", sortable: false },
        { text: "Env", value: "environment", sortable: false },
        { text: "Input Owner", value: "inputOwnerRef", sortable: false },
        { text: "Projected Owner", value: "projection.ownerRef", sortable: false },
        { text: "Unresolved", value: "unresolved", sortable: false },
        { text: "Reason", value: "reason", sortable: false },
        { text: "Ambiguous", value: "ambiguous", sortable: false },
        { text: "Candidates", value: "candidateCount", sortable: false },
        { text: "Selection", value: "selection", sortable: false },
      ],
      snackbar: {
        visible: false,
        text: "",
        color: "#006c27",
      },
    };
  },
  computed: {
    isUserAdmin() {
      return !!(
        this.$userData &&
        this.$userData.cass &&
        this.$userData.cass.isUserAdmin
      );
    },
    currentRun() {
      return this.refreshStatus.current || {};
    },
    lastRun() {
      return this.refreshStatus.last || {};
    },
    activeRefreshState() {
      return this.currentRun.state || this.lastRun.state || "idle";
    },
    activeStage() {
      return this.currentRun.stage || "";
    },
    maxPage() {
      if (this.total === 0) {
        return 1;
      }
      return Math.ceil(this.total / this.pageSize);
    },
    appOwnersMaxPage() {
      if (this.appOwnersTotal === 0) {
        return 1;
      }
      return Math.ceil(this.appOwnersTotal / this.appOwnersPageSize);
    },
  },
  mounted() {
    if (!this.isUserAdmin) {
      return;
    }
    this.loadState();
    this.search();
    this.searchAppOwners();
    this.pollHandle = setInterval(() => {
      if (this.isUserAdmin) {
        this.loadState(false);
      }
    }, 5000);
  },
  beforeDestroy() {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
    }
  },
  methods: {
    boolText(value) {
      return value ? "Y" : "N";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const dateValue = new Date(value);
      if (Number.isNaN(dateValue.getTime())) {
        return value;
      }
      return dateValue.toLocaleString();
    },
    formatPercent(value) {
      if (typeof value !== "number") {
        return "0.0%";
      }
      return `${value.toFixed(1)}%`;
    },
    formatDuration(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return `${value} ms`;
    },
    refreshStateColor(value) {
      const normalized = String(value || "idle").toLowerCase();
      if (normalized === "running") {
        return "#ef6c00";
      }
      if (normalized === "failed") {
        return "#c62828";
      }
      if (normalized === "success" || normalized === "completed") {
        return "#2e7d32";
      }
      return "#546e7a";
    },
    refreshStateIcon(value) {
      const normalized = String(value || "idle").toLowerCase();
      if (normalized === "running") {
        return "mdi-progress-clock";
      }
      if (normalized === "failed") {
        return "mdi-alert-circle";
      }
      if (normalized === "success" || normalized === "completed") {
        return "mdi-check-circle";
      }
      return "mdi-clock-outline";
    },
    showSnackbar(text, color = "#006c27") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    detailJson(value) {
      if (!value) {
        return "-";
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch (_error) {
        return String(value);
      }
    },
    ambiguityAlternateLines(projection) {
      const alternates = Array.isArray(projection && projection.candidates)
        ? projection.candidates
        : [];
      return alternates.map((alternate) => formatAmbiguityAlternateLine(alternate));
    },
    openCacheDetail(item) {
      this.detailDialog = {
        visible: true,
        source: "cache",
        sourceLabel: "Resolver Cache",
        title: "Resolver Cache Entry",
        item,
      };
    },
    openAppOwnerDetail(item) {
      this.detailDialog = {
        visible: true,
        source: "app-owner",
        sourceLabel: "Application Owner Reference",
        title: "Application Owner Entry",
        item,
      };
    },
    closeDetailDialog() {
      this.detailDialog.visible = false;
    },
    async loadState(showLoader = true) {
      if (!this.isUserAdmin) {
        return;
      }
      if (showLoader) {
        this.stateLoading = true;
      }
      try {
        const [cacheResponse, refreshResponse] = await Promise.all([
          cass.getIdentityResolverCacheState(),
          cass.getIdentityResolverRefreshState(),
        ]);
        this.cacheState = cacheResponse.data || this.cacheState;
        this.refreshStatus =
          refreshResponse.data || this.cacheState.refreshStatus || this.refreshStatus;
      } catch (error) {
        this.showSnackbar(cass.handleError(error).message, "#b71c1c");
      } finally {
        this.stateLoading = false;
      }
    },
    async triggerRefresh() {
      this.triggerLoading = true;
      try {
        await cass.triggerIdentityResolverRefresh();
        this.showSnackbar("Identity resolver refresh triggered");
        await this.loadState(false);
      } catch (error) {
        this.showSnackbar(cass.handleError(error).message, "#b71c1c");
      } finally {
        this.triggerLoading = false;
      }
    },
    async search() {
      this.searchLoading = true;
      try {
        const response = await cass.searchIdentityResolverCache(
          this.query,
          this.page,
          this.pageSize,
          this.cacheUnresolvedOnly,
          this.cacheAmbiguousOnly
        );
        this.searchItems = response.data.items || [];
        this.total = response.data.total || 0;
      } catch (error) {
        this.showSnackbar(cass.handleError(error).message, "#b71c1c");
      } finally {
        this.searchLoading = false;
      }
    },
    async searchAppOwners() {
      this.appOwnersLoading = true;
      try {
        const response = await cass.searchIdentityResolverAppOwners(
          this.appOwnersQuery,
          this.appOwnersPage,
          this.appOwnersPageSize,
          this.appOwnersUnresolvedOnly,
          this.appOwnersAmbiguousOnly
        );
        this.appOwnerItems = response.data.items || [];
        this.appOwnersTotal = response.data.total || 0;
      } catch (error) {
        this.showSnackbar(cass.handleError(error).message, "#b71c1c");
      } finally {
        this.appOwnersLoading = false;
      }
    },
    resetSearch() {
      this.query = "";
      this.page = 1;
      this.cacheUnresolvedOnly = false;
      this.cacheAmbiguousOnly = false;
      this.search();
    },
    resetAppOwnerSearch() {
      this.appOwnersQuery = "";
      this.appOwnersPage = 1;
      this.appOwnersUnresolvedOnly = false;
      this.appOwnersAmbiguousOnly = false;
      this.searchAppOwners();
    },
    changePage(nextPage) {
      this.page = nextPage;
      this.search();
    },
    changeAppOwnerPage(nextPage) {
      this.appOwnersPage = nextPage;
      this.searchAppOwners();
    },
  },
};
</script>

<style scoped>
.detail-json {
  max-height: 340px;
  overflow: auto;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
}

.diag-header-card {
  height: 100%;
  min-height: 152px;
  border-radius: 10px;
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
  box-shadow: 0 2px 8px rgba(38, 50, 56, 0.08);
}

.diag-header-card--success {
  border-top: 3px solid #2e7d32;
}

.diag-header-card--danger {
  border-top: 3px solid #c62828;
}

.diag-header-card--info {
  border-top: 3px solid #1565c0;
}

.diag-header-card--neutral {
  border-top: 3px solid #6d4c41;
}

.diag-metric-label {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 11px;
  font-weight: 700;
  color: #546e7a;
}

.diag-chip-row {
  margin: 8px 0;
}

.diag-chip-row--stacked {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.diag-metric-value {
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
  color: #263238;
  margin-top: 4px;
}

.diag-metric-meta {
  font-size: 12px;
  color: #546e7a;
}

.diag-metric-error {
  margin-top: 8px;
  font-size: 12px;
  color: #b71c1c;
  font-weight: 600;
}

.diag-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.diag-stat-item {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 8px;
  padding: 10px 8px;
}

.diag-stat-value {
  font-size: 20px;
  line-height: 1.1;
  font-weight: 700;
  color: #263238;
}

.diag-stat-label {
  margin-top: 4px;
  font-size: 11px;
  color: #607d8b;
}

.diag-inline-stats {
  margin-top: 4px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 11px;
  color: #455a64;
}

.diag-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #37474f;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.diag-run-block {
  padding: 2px 0;
}

.diag-run-title {
  font-size: 12px;
  font-weight: 700;
  color: #455a64;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.diag-kv-grid {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  column-gap: 12px;
  row-gap: 4px;
}

.diag-kv-key {
  font-size: 12px;
  color: #607d8b;
}

.diag-kv-value {
  font-size: 12px;
  color: #263238;
  font-weight: 500;
  word-break: break-word;
}

.diag-progress-wrap {
  margin-top: 10px;
}

.diag-progress-label {
  margin-bottom: 6px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #455a64;
}

.resolver-search-row {
  padding-top: 4px;
  margin-bottom: 12px;
}

@media (max-width: 960px) {
  .diag-stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .diag-kv-grid {
    grid-template-columns: 92px minmax(0, 1fr);
  }

  .diag-header-card {
    min-height: 0;
  }
}
</style>
